/**
 * AssistantsPage - Interface unifiée pour tous les agents IA
 * Agents: Comptabilité, Chef Exploitation, Commercial, RH, Stocks
 */
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Calculator,
  Users,
  Package,
  TrendingUp,
  UserCircle,
  Send,
  Loader2,
  Bot,
  Sparkles,
  MessageSquare,
  ChevronRight,
  Clock,
  AlertTriangle,
  Euro,
  BarChart3,
  Calendar,
  Clipboard,
  Mic,
  MicOff,
  Square,
} from 'lucide-react';
import api from '@/lib/api';

const AGENTS = [
  {
    id: 'direction',
    nom: 'Assistante Direction',
    icon: Sparkles,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    description: 'Je comprends tout et gère le CRM automatiquement',
    principal: true,
    suggestions: [
      "Fais les factures février",
      "J'ai eu un appel de Dupont, il veut un devis pour 30ha",
      "Combien facturer ce mois ?",
      "Heures des salariés ce mois",
      "Quelles sont les priorités ?"
    ],
    actions: [
      { label: "📝 Facturer ce mois", query: "Fais les factures" },
      { label: "📞 Enregistrer appel", query: "J'ai eu un appel de " },
      { label: "📊 Récap à facturer", query: "Combien facturer ce mois" },
    ]
  },
  {
    id: 'comptabilite',
    nom: 'Comptabilité',
    icon: Calculator,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    description: 'Facturation, heures, tarifs, paie - EXÉCUTE les actions',
    suggestions: [
      "Fais les factures janvier",
      "Combien facturer ce mois ?",
      "Heures des salariés par client",
      "Reste à encaisser",
      "Tarifs clients"
    ],
    actions: [
      { label: "🧾 Générer factures", query: "Fais les factures" },
      { label: "💰 Reste à encaisser", query: "Reste à encaisser" },
    ]
  },
  {
    id: 'chef_exploitation',
    nom: 'Chef Exploitation',
    icon: UserCircle,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    description: 'Vision globale, alertes, planning',
    suggestions: [
      "Quelles sont les priorités ?",
      "Résumé de situation",
      "Planning de la semaine",
      "Y a-t-il des alertes ?"
    ]
  },
  {
    id: 'commercial',
    nom: 'Commercial',
    icon: TrendingUp,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    description: 'CRM, clients, ventes',
    suggestions: [
      "Clients à relancer",
      "Top clients de l'année",
      "Pipeline prospects",
      "Opportunités commerciales"
    ]
  },
  {
    id: 'rh',
    nom: 'RH',
    icon: Users,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    description: 'Personnel, heures, congés',
    suggestions: [
      "Équipe et effectifs",
      "Heures du mois",
      "Congés planifiés",
      "Formations à renouveler"
    ]
  },
  {
    id: 'stocks',
    nom: 'Stocks',
    icon: Package,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    description: 'Inventaire, mouvements',
    suggestions: [
      "Alertes rupture stock",
      "Inventaire complet",
      "Stock carburant",
      "Stock au 1er janvier"
    ]
  },
  {
    id: 'mecanicien',
    nom: 'Mécanicien',
    icon: Clipboard,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    description: 'Documentation technique, diagnostic pannes, entretien matériel',
    suggestions: [
      "Comment changer le filtre à huile du John Deere ?",
      "Quelle huile pour ce tracteur ?",
      "Le moteur fait un bruit anormal",
      "Procédure de vidange",
      "Fréquence d'entretien recommandée"
    ],
    actions: [
      { label: "🔧 Diagnostic panne", query: "Le tracteur présente un problème: " },
      { label: "📋 Plan entretien", query: "Plan d'entretien pour " },
      { label: "📖 Documentation", query: "Documentation technique pour " },
    ]
  }
];

const AssistantsPage = () => {
  const [selectedAgent, setSelectedAgent] = useState(AGENTS[0]);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [contextType, setContextType] = useState('eta');
  const messagesEndRef = useRef(null);
  
  // États pour la dictée vocale
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const sendQuestionRef = useRef(null);

  // Lire les paramètres URL pour pré-sélectionner un agent
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const agentParam = params.get('agent');
    const machineId = params.get('machine');
    const marque = params.get('marque');
    const modele = params.get('modele');
    
    if (agentParam) {
      const agent = AGENTS.find(a => a.id === agentParam);
      if (agent) {
        setSelectedAgent(agent);
        
        // Si c'est le mécanicien avec des infos machine, préparer le contexte
        if (agentParam === 'mecanicien' && (marque || modele)) {
          const machineInfo = [marque, modele].filter(Boolean).join(' ');
          setInputValue(`Documentation technique pour ${machineInfo}`);
        }
      }
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Message de bienvenue quand on change d'agent
  useEffect(() => {
    setMessages([{
      type: 'agent',
      agent: selectedAgent.id,
      content: `Bonjour ! Je suis l'assistant ${selectedAgent.nom}. ${selectedAgent.description}. Comment puis-je vous aider ?`,
      timestamp: new Date()
    }]);
  }, [selectedAgent]);
  
  // Initialiser la reconnaissance vocale (une seule fois)
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'fr-FR';
      
      recognitionRef.current.onresult = (event) => {
        let finalText = '';
        let interimText = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalText += t;
          } else {
            interimText += t;
          }
        }
        
        if (finalText) {
          setInputValue(finalText);
          setTranscript(finalText);
          finalTranscriptRef.current = finalText;
        } else {
          setInputValue(interimText);
        }
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
        // Utilise le ref pour avoir la valeur à jour (pas de stale closure)
        const text = finalTranscriptRef.current;
        if (text && text.trim()) {
          finalTranscriptRef.current = '';
          setTranscript('');
          if (sendQuestionRef.current) sendQuestionRef.current(text);
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Erreur reconnaissance vocale:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone non autorisé', {
            description: 'Veuillez autoriser l\'accès au microphone'
          });
        }
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch(e) {}
      }
    };
  }, []);
  
  // Démarrer/arrêter la dictée vocale
  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error('Reconnaissance vocale non supportée', {
        description: 'Votre navigateur ne supporte pas la dictée vocale'
      });
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      finalTranscriptRef.current = '';
      setTranscript('');
      setInputValue('');
      recognitionRef.current.start();
      setIsListening(true);
      toast.info('Parlez maintenant...', {
        description: 'Je vous écoute'
      });
    }
  };

  const sendQuestion = async (question) => {
    if (!question.trim()) return;

    // Ajouter le message utilisateur
    const userMessage = {
      type: 'user',
      content: question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await api.post('/agents/question', {
        agent_type: selectedAgent.id,
        question: question,
        contexte_type: contextType
      });

      const agentMessage = {
        type: 'agent',
        agent: selectedAgent.id,
        content: response.data.reponse || "Je n'ai pas pu traiter votre demande.",
        data: response.data.data,
        action: response.data.action_possible,
        actionExecutee: response.data.action_executee,
        delegueA: response.data.delegue_a,
        suggestion: response.data.suggestion,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, agentMessage]);
      
      // Notification si action exécutée
      if (response.data.action_executee) {
        toast.success('Action exécutée avec succès !');
      }

    } catch (error) {
      console.error('Erreur agent:', error);
      const errorMessage = {
        type: 'agent',
        agent: selectedAgent.id,
        content: "Désolé, une erreur s'est produite. Réessayez.",
        error: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Erreur de communication avec l\'agent');
    } finally {
      setLoading(false);
    }
  };

  // Garder la ref à jour pour que le callback onend puisse appeler sendQuestion
  sendQuestionRef.current = sendQuestion;

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(inputValue);
  };

  const handleSuggestion = (suggestion) => {
    sendQuestion(suggestion);
  };

  const formatMessage = (content) => {
    // Convertir le markdown basique en HTML
    return content
      .split('\n')
      .map((line, i) => {
        // Titres
        if (line.startsWith('**') && line.endsWith('**')) {
          return <h4 key={i} className="font-bold text-base mt-2">{line.replace(/\*\*/g, '')}</h4>;
        }
        // Sous-titres avec **
        if (line.includes('**')) {
          const parts = line.split('**');
          return (
            <p key={i} className="text-sm">
              {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
            </p>
          );
        }
        // Listes
        if (line.startsWith('•') || line.startsWith('-')) {
          return <p key={i} className="text-sm pl-2">{line}</p>;
        }
        // Sous-listes
        if (line.startsWith('  └') || line.startsWith('   └')) {
          return <p key={i} className="text-xs text-muted-foreground pl-6">{line.trim()}</p>;
        }
        // Ligne vide
        if (!line.trim()) {
          return <br key={i} />;
        }
        return <p key={i} className="text-sm">{line}</p>;
      });
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex" data-testid="assistants-page">
      {/* Sidebar Agents */}
      <div className="w-64 border-r bg-muted/30 p-4 flex flex-col">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Bot className="w-5 h-5" />
          Assistants IA
        </h2>
        
        <div className="space-y-2 flex-1">
          {AGENTS.map(agent => {
            const Icon = agent.icon;
            const isSelected = selectedAgent.id === agent.id;
            
            return (
              <button
                key={agent.id}
                onClick={() => setSelectedAgent(agent)}
                className={`w-full p-3 rounded-lg text-left transition-all ${
                  isSelected 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted'
                }`}
                data-testid={`agent-${agent.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary-foreground/20' : agent.bgColor}`}>
                    <Icon className={`w-4 h-4 ${isSelected ? 'text-primary-foreground' : agent.color}`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{agent.nom}</p>
                    <p className={`text-xs ${isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      {agent.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Toggle Contexte */}
        <div className="mt-4 p-3 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground mb-2">Contexte</p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={contextType === 'eta' ? 'default' : 'outline'}
              onClick={() => setContextType('eta')}
              className="flex-1 text-xs"
            >
              ETA
            </Button>
            <Button
              size="sm"
              variant={contextType === 'exploitation' ? 'default' : 'outline'}
              onClick={() => setContextType('exploitation')}
              className="flex-1 text-xs"
            >
              Exploitation
            </Button>
          </div>
        </div>
      </div>

      {/* Zone de Chat */}
      <div className="flex-1 flex flex-col">
        {/* Header Agent */}
        <div className="border-b p-4 flex items-center gap-3">
          <div className={`p-2 rounded-lg ${selectedAgent.bgColor}`}>
            <selectedAgent.icon className={`w-5 h-5 ${selectedAgent.color}`} />
          </div>
          <div>
            <h3 className="font-semibold">{selectedAgent.nom}</h3>
            <p className="text-sm text-muted-foreground">{selectedAgent.description}</p>
          </div>
          <Badge variant="outline" className="ml-auto">
            <Sparkles className="w-3 h-3 mr-1" />
            IA
          </Badge>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4 max-w-3xl mx-auto">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-4 ${
                    msg.type === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : msg.error
                        ? 'bg-destructive/10 border border-destructive/20'
                        : 'bg-muted'
                  }`}
                >
                  {msg.type === 'agent' && (
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Bot className="w-4 h-4" />
                      <span className="text-xs font-medium">Assistant {selectedAgent.nom}</span>
                      {msg.delegueA && (
                        <Badge variant="secondary" className="text-xs">
                          via {msg.delegueA}
                        </Badge>
                      )}
                      {msg.actionExecutee && (
                        <Badge variant="default" className="text-xs bg-green-500">
                          ✓ Action exécutée
                        </Badge>
                      )}
                    </div>
                  )}
                  <div className="space-y-1">
                    {formatMessage(msg.content)}
                  </div>
                  
                  {/* Action possible */}
                  {msg.action && (
                    <Button size="sm" variant="outline" className="mt-3">
                      <ChevronRight className="w-4 h-4 mr-1" />
                      {msg.action === 'generer_factures' && 'Générer les factures'}
                      {msg.action === 'valider_heures' && 'Valider les heures'}
                      {msg.action === 'generer_recap' && 'Générer le récapitulatif'}
                      {msg.action === 'commander' && 'Passer commande'}
                    </Button>
                  )}
                  
                  {/* Suggestion d'action */}
                  {msg.suggestion && !msg.actionExecutee && (
                    <Button 
                      size="sm" 
                      variant="default" 
                      className="mt-3"
                      onClick={() => sendQuestion(msg.suggestion)}
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      {msg.suggestion}
                    </Button>
                  )}
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    {msg.timestamp.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg p-4 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Réflexion en cours...</span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Suggestions */}
        <div className="border-t p-3 bg-muted/30">
          <div className="flex flex-wrap gap-2 max-w-3xl mx-auto">
            {selectedAgent.suggestions.map((suggestion, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => handleSuggestion(suggestion)}
                disabled={loading}
                className="text-xs"
              >
                {suggestion}
              </Button>
            ))}
          </div>
        </div>

        {/* Input avec bouton micro */}
        <div className="border-t p-4">
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={isListening ? "Je vous écoute..." : `Posez votre question à l'assistant ${selectedAgent.nom}...`}
              disabled={loading}
              className={`flex-1 ${isListening ? 'border-red-500 animate-pulse' : ''}`}
              data-testid="chat-input"
            />
            <Button 
              type="button" 
              variant={isListening ? "destructive" : "outline"}
              onClick={toggleListening}
              disabled={loading}
              data-testid="mic-btn"
              title={isListening ? "Arrêter la dictée" : "Dictée vocale"}
            >
              {isListening ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </Button>
            <Button type="submit" disabled={loading || !inputValue.trim()} data-testid="send-btn">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
          {isListening && (
            <p className="text-xs text-center text-red-500 mt-2 animate-pulse">
              🎙️ Enregistrement en cours... Parlez maintenant
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssistantsPage;
